/* The active warehouse, app-wide.
 *
 * One selection, made in the topbar, that every screen reads — replacing the
 * picker each screen used to carry. A pharmacy head switches store once and
 * Inventory, Racks, the GRN, billing and every report follow; a PH user is pinned
 * to their own store and never sees a control.
 *
 * The value itself lives in warehouseContext (localStorage), because services and
 * non-React code read it too — `getResolvedWarehouseCode()` is that same value.
 * This provider is the React view of it: it holds the store list, resolves a PH
 * user's code if login didn't, and re-renders the app when the selection changes.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import supplyService from "../../Services/supplyService";
import {
  ALL_STORES,
  getResolvedWarehouseCode,
  getWarehouseInfo,
  ensureResolvedWarehouseCode,
  isAllStores,
  setActiveWarehouse,
  subscribeActiveWarehouse,
  isPharmacyHead,
} from "../../Services/warehouseContext";

const WarehouseCtx = createContext(null);

export const WarehouseProvider = ({ children }) => {
  const [code, setCode] = useState(() => getResolvedWarehouseCode());
  /* "All stores" is a separate flag rather than a magic value in `code`, so a
     screen scoping a fetch just uses `code` ("" = no filter = every store) and only
     screens that NEED one store have to think about it. */
  const [all, setAll] = useState(() => isAllStores());
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const isHead = isPharmacyHead();

  /* Anything that switches the store — the topbar picker, or a service reacting
     to something — notifies here, so one subscription keeps the whole app in step. */
  useEffect(
    () =>
      subscribeActiveWarehouse((next) => {
        setAll(next === ALL_STORES);
        setCode(next === ALL_STORES ? "" : next);
      }),
    []
  );

  /* The list every picker needs. Cached in supplyService, so this is one call per
     session however often the provider mounts. */
  useEffect(() => {
    let alive = true;
    supplyService
      .getWarehouses()
      .then((list) => alive && setWarehouses(list || []))
      .catch(() => alive && setWarehouses([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  /* A PH user's own store may not be in localStorage yet on a cold load (a session
     that signed in before warehouse resolution shipped, or any gap). Resolving it
     here means no screen has to.

     Never for a head: their empty selection is deliberate, and resolving their
     login warehouse into it would silently answer the question the topbar is
     asking them. */
  useEffect(() => {
    if (code || all || isHead) return undefined;
    let alive = true;
    ensureResolvedWarehouseCode().then((resolved) => {
      if (alive && resolved) setCode(resolved);
    });
    return () => {
      alive = false;
    };
  }, [code, all, isHead]);

  /* Switch store. Takes a code, resolves it against the loaded list so the full
     row (name, GSTIN, DL) is stored — the invoice header reads those.

     Then RELOADS THE PAGE. The selection is persisted first, so the app comes back
     up already in the new store and every screen fetches from scratch. Two reasons
     to be this blunt rather than relying on re-render:

       · Freshness. A reload drops every module-level cache (the warehouse and
         medicine lists in supplyService) and every piece of component state — page
         numbers, expanded rows, computed totals, a stale KPI. Re-rendering only
         refetches what an effect happens to watch; a reload leaves nothing behind
         to be wrong.
       · One code path. Screens differ in how much they derive from a fetch, and a
         missed dependency shows up as a figure from the previous store, which is
         the worst kind of bug here — plausible and wrong.

     The cost: anything unsaved is discarded, so a half-entered GRN or counter sale
     is lost. Switching store mid-document already invalidated it (the store, its
     suppliers and its racks all change), so nothing salvageable is being thrown
     away — but it is why the same store is a no-op below rather than a reload. */
  const select = useCallback(
    (nextCode) => {
      const current = isAllStores() ? ALL_STORES : getResolvedWarehouseCode();
      const next = nextCode || "";
      /* Re-picking what is already selected must not wipe the screen. */
      if (String(next) === String(current)) return;

      if (next === ALL_STORES) {
        setActiveWarehouse(ALL_STORES);
        setAll(true);
        setCode("");
      } else if (!next) {
        setAll(false);
        setCode(setActiveWarehouse(null));
      } else {
        setAll(false);
        const row = warehouses.find((w) => String(w.id) === String(next));
        setCode(setActiveWarehouse(row || { id: next }));
      }

      /* State was set above so nothing flashes stale in the frame before the
         browser tears the page down. */
      window.location.reload();
    },
    [warehouses]
  );

  const value = useMemo(
    () => ({
      /* The warehouse id every endpoint expects. "" only for a head who has not
         chosen yet, or an unresolved PH login — screens show their empty state. */
      code,
      /* True when every store is in scope. `code` is "" in that case, so a fetch
         that just spreads it in gets all warehouses for free; a screen that needs
         one store checks this to say "pick a single store" rather than "pick a
         store", which is a different instruction. */
      isAll: all,
      /* Display name for the current scope, falling back to the code. Empty when
         nothing is selected — the getWarehouseInfo() fallback below is the LOGIN
         store, and naming it while `code` is empty would put a store's name on a
         screen that is deliberately scoped to nothing. */
      name: all
        ? "All stores"
        : !code
        ? ""
        : warehouses.find((w) => String(w.id) === String(code))?.name ||
          getWarehouseInfo()?.pharmacyName ||
          code,
      warehouses,
      loading,
      isHead,
      select,
    }),
    [code, all, warehouses, loading, isHead, select]
  );

  return <WarehouseCtx.Provider value={value}>{children}</WarehouseCtx.Provider>;
};

/* `const { code } = useWarehouse()` is all most screens need: it is the store to
   scope every fetch to, and it changes when the topbar changes.
   Outside the provider (a test, a stray render) it degrades to the persisted value
   rather than throwing, so a screen still scopes correctly. */
export const useWarehouse = () => {
  const ctx = useContext(WarehouseCtx);
  return (
    ctx || {
      code: getResolvedWarehouseCode(),
      isAll: isAllStores(),
      name: getWarehouseInfo()?.pharmacyName || "",
      warehouses: [],
      loading: false,
      isHead: isPharmacyHead(),
      select: () => {},
    }
  );
};

export default WarehouseProvider;
